import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Stage } from "@/components/Stage";
import { Eyebrow } from "@/components/Wordmark";

const EASE = [0.22, 1, 0.36, 1];

const PRACTICE = [
  "Personal Injury",
  "Mass Tort",
  "Medical Malpractice",
  "Workers' Comp",
  "Social Security",
  "Employment",
  "Estate Planning",
];

const INTEGRATIONS = [
  "Litify",
  "Filevine",
  "SmartAdvocate",
  "Salesforce",
  "RingCentral",
  "Five9",
  "Dialpad",
  "Zoom",
];

export const FitAppendixSlide = () => {
  const reduce = useReducedMotion();

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: 0.05 } },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 18 },
    show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE } },
  };

  return (
    <Stage testId="appendix-agents">
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="h-full w-full flex flex-col px-[clamp(28px,6%,84px)] py-[clamp(18px,3.6%,40px)]"
      >
        <motion.div variants={item} className="flex items-center justify-between">
          <Eyebrow>Appendix · Fit</Eyebrow>
          <span className="font-mono text-xs tracking-[0.24em] text-accent tabular-nums">
            A · 3 of 3
          </span>
        </motion.div>

        <motion.h1
          variants={item}
          className="font-display font-medium text-3xl sm:text-4xl leading-[1.06] text-ink mt-3"
        >
          Built for your practice — and your stack.
        </motion.h1>
        <motion.p variants={item} className="font-sans text-[14px] text-ink-soft mt-2 max-w-[72ch]">
          Agents are tuned to the language, workflow, and intake of your practice
          area, and connect to the tools you already run.
        </motion.p>

        <div className="flex-1 min-h-0 grid grid-cols-1 min-[900px]:grid-cols-[1.3fr_1fr] gap-6 mt-5">
          {/* LEFT practice areas */}
          <motion.div variants={item}>
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-muted mb-3">
              Practice areas we serve
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {PRACTICE.map((p) => (
                <div
                  key={p}
                  className="flex items-center gap-2.5 rounded-lg border border-hairline bg-[#FBFAF6] px-3 py-2.5"
                >
                  <span className="w-2.5 h-2.5 bg-accent rotate-45 shrink-0" />
                  <span className="font-sans text-[13px] text-ink">{p}</span>
                </div>
              ))}
              <div className="flex items-center gap-2.5 rounded-lg border border-dashed border-accent/40 px-3 py-2.5">
                <span className="w-2.5 h-2.5 border border-accent rotate-45 shrink-0" />
                <span className="font-sans text-[13px] text-accent">+ more, tuned to you</span>
              </div>
            </div>
          </motion.div>

          {/* RIGHT stack */}
          <div className="flex flex-col gap-4 min-h-0">
            <motion.div
              variants={item}
              className="rounded-2xl p-5 text-paper bg-[linear-gradient(155deg,#191622_0%,#211D2A_100%)]"
            >
              <h3 className="font-display font-medium text-lg text-paper">
                Connects to your stack
              </h3>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {INTEGRATIONS.map((n) => (
                  <span
                    key={n}
                    className="font-mono text-[10px] uppercase tracking-[0.08em] text-paper/75 border border-white/15 rounded-full px-2.5 py-1"
                  >
                    {n}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              variants={item}
              className="rounded-2xl border border-hairline bg-[#FBFAF6] p-5 flex-1"
            >
              <h3 className="font-display font-medium text-lg text-ink">
                Tuned per practice area
              </h3>
              <p className="font-sans text-[13px] text-ink-soft leading-relaxed mt-2">
                A PI intake and a workers' comp intake ask different questions and
                follow different rules. Agents are built to yours — not a generic
                script.
              </p>
            </motion.div>
          </div>
        </div>

        <motion.div variants={item} className="border-t border-hairline pt-4 mt-4">
          <p className="font-display text-lg sm:text-xl leading-snug text-ink">
            Your firm, your intake, your language —{" "}
            <span className="text-accent">the agent just sounds like you.</span>
          </p>
        </motion.div>
      </motion.div>
    </Stage>
  );
};

export default FitAppendixSlide;
